import {
  IonButton,
  IonContent,
  IonFooter,
  IonPage,
  IonRippleEffect,
  IonToolbar
} from "@ionic/react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import AvatarUpload from "../AvatarUpload";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { updateUserAvatar } from '@/utils/imageUpload';
import { useOnboarding } from "@/hooks/use-onboarding";
import HorizontalScrollMedia from "../HorizontalScrollMedia";

const AvatarSetup: React.FC<{ nextStep: string; }> = ({ nextStep }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { updateOnboardingData } = useOnboarding();
  const [user, setUser] = useState<any>(null);
  const [avatar, setAvatar] = useState<string | null>(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      const result = await FirebaseAuthentication.getCurrentUser();
      setUser(result.user);
      setAvatar(result.user?.photoURL || null);
    };
    getCurrentUser();
  }, []);

  const handleAvatarClick = async (avatarPath: string) => {
    try {
      const response = await fetch(avatarPath);
      const blob = await response.blob();

      // Convert blob to base64
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.readAsDataURL(blob);
      });

      const imageUrl = await updateUserAvatar(base64);
      setAvatar(imageUrl);
      await updateOnboardingData({ imageUrl });

      history.push(nextStep);
    } catch (error) {
      console.error('Error updating avatar:', error);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col md:pt-16 text-center items-center bg-card h-full md:justify-center"
        >
          <div className="mb-6">
            <span className="text-3xl font-semibold mb-6">{t("profile_picture")}</span>
            <br />
            <h2 className="text-muted-foreground">{t("upload_profile_picture")}</h2>
          </div>
          {/* Avatar Upload */}
          <AvatarUpload key={avatar} initialImage={avatar} onAvatarUpdated={() => updateOnboardingData({ imageUrl: avatar })} size="lg" />
          <div className="m-8 border-t border-dashed w-96"></div>
          {/* Avatar images list */}
          <h2 className="mb-6 text-muted-foreground">{t("or_pick_one_from_the_list")}</h2>

          <div className="flex w-full justify-center items-start overflow-y-auto h-auto [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <HorizontalScrollMedia>
              {[...Array(8)].map((_, index) => {
                const avatarNumber = String(index + 1).padStart(2, '0');
                return (
                  <button className="ion-activatable ripple-parent rounded-full size-36 hover:transform hover:scale-110 transition-all duration-300" onClick={() => handleAvatarClick(`/assets/images/avatars/${avatarNumber}.png`)} key={avatarNumber}>
                    <img key={avatarNumber} src={`/assets/images/avatars/${avatarNumber}.png`} alt={`Avatar ${avatarNumber}`} />
                    <IonRippleEffect></IonRippleEffect>
                  </button>
                );
              }).reverse()}
            </HorizontalScrollMedia>
          </div>
          <IonButton
            shape="round"
            expand="block"
            className="mt-8 hidden md:block ion-activatable ripple-parent"
            onClick={() => history.push(nextStep)}
          >
            {t("next")}
          </IonButton>
        </motion.div>

      </IonContent>
      <IonFooter className="ion-no-border bg-card md:hidden">
        <IonToolbar>
          <IonButton
            shape="round"
            expand="block"
            className="m-4"
            onClick={() => history.push(nextStep)}
          >
            {t("next")}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage >
  );
};

export default AvatarSetup;